import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type {
  VendingTransaction,
  VendingTransactionSummary,
  VendingMachine,
  VendingItem,
  VendingDeduction,
  VendingSyncConfig,
} from "./VendingIntegration.types";

/**
 * Vending Integration Store
 * Manages integration with Jason's automated vending machine database
 * Allows tracking employee supply purchases and automatic payroll deductions
 */
class VendingIntegrationStore {
  rootStore: RootStore;

  // UI State
  selectedWorkerId: string | null = null;
  selectedPayPeriodId: string | null = null;
  isLoading = false;
  isSyncing = false;
  lastSyncAt: string | null = null;
  lastSyncError: string | null = null;

  // Data (would normally come from service stores)
  machines: VendingMachine[] = [];
  items: VendingItem[] = [];
  pendingDeductions: VendingDeduction[] = [];

  // Sync configuration
  syncConfig: VendingSyncConfig = {
    enabled: false,
    syncFrequency: "daily",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Computed Properties ====================

  get transactions(): VendingTransaction[] {
    return (this.rootStore.serviceStores?.["vending-transactions"]?.entities as VendingTransaction[]) || [];
  }

  get workerTransactions(): VendingTransaction[] {
    if (!this.selectedWorkerId) return [];
    return this.transactions.filter((t) => t.workerId === this.selectedWorkerId);
  }

  get workerSummary(): VendingTransactionSummary | null {
    if (!this.selectedWorkerId || !this.selectedPayPeriodId) return null;

    const periodTransactions = this.transactions.filter(
      (t) =>
        t.workerId === this.selectedWorkerId &&
        t.payPeriodId === this.selectedPayPeriodId
    );

    const freeTransactions = periodTransactions.filter((t) => t.isFreeAllocation);
    const chargeableTransactions = periodTransactions.filter((t) => !t.isFreeAllocation);

    return {
      workerId: this.selectedWorkerId,
      workerName: periodTransactions[0]?.workerName || this.selectedWorkerId,
      payPeriodId: this.selectedPayPeriodId,
      payPeriodStart: "", // Would come from pay period data
      payPeriodEnd: "",
      transactions: periodTransactions,
      totalTransactions: periodTransactions.length,
      freeAllocationUsed: freeTransactions.reduce((sum, t) => sum + t.quantity, 0),
      freeAllocationRemaining: 0, // Would calculate based on allocation
      chargeableAmount: chargeableTransactions.reduce((sum, t) => sum + t.totalCost, 0),
      status: "pending",
    };
  }

  get unprocessedTransactions(): VendingTransaction[] {
    return this.transactions.filter((t) => !t.isProcessed);
  }

  get totalPendingAmount(): number {
    return this.pendingDeductions.reduce((sum, d) => sum + d.amount, 0);
  }

  // ==================== Actions ====================

  setSelectedWorkerId = (workerId: string | null) => {
    this.selectedWorkerId = workerId;
  };

  setSelectedPayPeriodId = (payPeriodId: string | null) => {
    this.selectedPayPeriodId = payPeriodId;
  };

  // ==================== Data Loading ====================

  loadData = async () => {
    this.isLoading = true;
    try {
      // Load from vending-transactions service store
      const transactionStore = this.rootStore.serviceStores?.["vending-transactions"];
      if (transactionStore) {
        await transactionStore.find({ query: { $limit: 1000 } });
      }
    } catch (error) {
      console.error("Failed to load vending data:", error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // ==================== Sync Operations ====================

  syncFromVendingSystem = async (): Promise<boolean> => {
    this.isSyncing = true;
    this.lastSyncError = null;

    try {
      // This would call an API to sync from Jason's vending machine database
      // For now, simulating the sync process

      // 1. Fetch new transactions from vending system
      // 2. Match transactions to employees by ID/badge number
      // 3. Calculate free allocation vs chargeable
      // 4. Create/update transactions in our system

      runInAction(() => {
        this.lastSyncAt = new Date().toISOString();
      });

      await this.loadData();
      return true;
    } catch (error) {
      runInAction(() => {
        this.lastSyncError = error instanceof Error ? error.message : "Sync failed";
      });
      return false;
    } finally {
      runInAction(() => {
        this.isSyncing = false;
      });
    }
  };

  updateSyncConfig = (config: Partial<VendingSyncConfig>) => {
    this.syncConfig = { ...this.syncConfig, ...config };
  };

  // ==================== Deduction Management ====================

  createDeductionForWorker = async (
    workerId: string,
    payPeriodId: string
  ): Promise<VendingDeduction | null> => {
    const transactions = this.transactions.filter(
      (t) =>
        t.workerId === workerId &&
        t.payPeriodId === payPeriodId &&
        !t.isFreeAllocation &&
        !t.isProcessed
    );

    if (transactions.length === 0) return null;

    const totalAmount = transactions.reduce((sum, t) => sum + t.totalCost, 0);

    const deduction: VendingDeduction = {
      deductionId: `VD-${Date.now()}`,
      workerId,
      payPeriodId,
      amount: totalAmount,
      transactionIds: transactions.map((t) => t.transactionId),
      status: "pending",
      createdAt: new Date().toISOString(),
    };

    this.pendingDeductions.push(deduction);
    return deduction;
  };

  approveDeduction = async (deductionId: string, approvedBy: string): Promise<boolean> => {
    const deduction = this.pendingDeductions.find((d) => d.deductionId === deductionId);
    if (!deduction) return false;

    deduction.status = "approved";
    deduction.approvedBy = approvedBy;
    deduction.approvedAt = new Date().toISOString();

    return true;
  };

  processDeductions = async (payPeriodId: string): Promise<number> => {
    const approvedDeductions = this.pendingDeductions.filter(
      (d) => d.payPeriodId === payPeriodId && d.status === "approved"
    );

    let processedCount = 0;

    for (const deduction of approvedDeductions) {
      // Mark transactions as processed
      for (const transactionId of deduction.transactionIds) {
        const transaction = this.transactions.find((t) => t.transactionId === transactionId);
        if (transaction) {
          transaction.isProcessed = true;
          transaction.deductionId = deduction.deductionId;
        }
      }

      deduction.status = "deducted";
      processedCount++;
    }

    return processedCount;
  };

  // ==================== Reporting ====================

  getUsageReport = (startDate: string, endDate: string, plantIds?: string[]) => {
    let filteredTransactions = this.transactions.filter(
      (t) => t.transactionDate >= startDate && t.transactionDate <= endDate
    );

    if (plantIds && plantIds.length > 0) {
      const machines = this.machines.filter((m) => plantIds.includes(m.plantId));
      const machineIds = machines.map((m) => m.machineId);
      filteredTransactions = filteredTransactions.filter((t) =>
        machineIds.includes(t.machineId)
      );
    }

    const totalCost = filteredTransactions.reduce((sum, t) => sum + t.totalCost, 0);
    const freeItems = filteredTransactions.filter((t) => t.isFreeAllocation);
    const chargeableItems = filteredTransactions.filter((t) => !t.isFreeAllocation);

    return {
      totalTransactions: filteredTransactions.length,
      totalCost,
      totalFreeItems: freeItems.reduce((sum, t) => sum + t.quantity, 0),
      totalChargeableItems: chargeableItems.reduce((sum, t) => sum + t.quantity, 0),
      chargeableAmount: chargeableItems.reduce((sum, t) => sum + t.totalCost, 0),
    };
  };
}

export default VendingIntegrationStore;
